import PropTypes from "prop-types";
import React, { useCallback, useMemo } from "react";
import { imageResizer } from "../../lib/utils";
import { ErrorHandler } from "./ErrorHandler";
import { PromoState } from "../../globals";
import { useVideoDataContext } from "../contexts/VideoDataContext";
import { useStickyVideoContext } from "../contexts/StickyVideoContext";
import { useOrcaCommonContext } from "../contexts/OrcaCommonContext.hooks";
import { getWrapperBackgroundImageProps } from "./Wrapper/utils";

export const useCalcCss = ({
  isOpenSticky,
  aspectRatio,
  aspectMatch,
  blur,
  color
}) => {
  const calcCss = useCallback(() => {
    const commonStyles = {
      width: "100%",
      position: "relative",
      backgroundPosition: "center",
      backgroundSize: aspectMatch ? "cover" : "contain",
      paddingBottom: `calc(${aspectRatio}*100%)`
    };

    const stickyStyles = {
      ...commonStyles,
      backgroundColor: "unset",
      backgroundImage: "none"
    };

    const nonStickyStyles = {
      ...commonStyles,
      backgroundRepeat: "no-repeat",
      backgroundColor: color || "black",
      ...(blur
        ? {
            filter: "blur(5px)",
            transition: "filter .1s"
          }
        : {})
    };

    return isOpenSticky ? stickyStyles : nonStickyStyles;
  }, [isOpenSticky, aspectRatio, aspectMatch, blur, color]);

  return calcCss;
};

export const useBackgroundImage = ({
  placeholderImage,
  resize,
  videoData,
  originalVideo
}) => {
  const getImageWidth = useCallback(() => {
    // NOTE: May require sync up with the spectrum code
    // see https://github.com/WashPost/spectrum/blob/main/components/document-elements/shared/lede-art-preload/index.jsx#L54
    return videoData?.additional_properties?.vertical ? 480 : 960;
  }, [videoData?.additional_properties?.vertical]);

  const isFirstVideo = (videoData, originalVideo) => {
    return !originalVideo?._id || videoData?._id === originalVideo?._id;
  };

  return useCallback(() => {
    const imageWidth = getImageWidth(videoData);

    if (placeholderImage && isFirstVideo(videoData, originalVideo)) {
      return resize
        ? imageResizer(placeholderImage, imageWidth)
        : placeholderImage;
    }

    const videoImage = videoData?.bandito?.image || videoData?.promo_image?.url;
    return videoImage ? imageResizer(videoImage, imageWidth) : null;
  }, [placeholderImage, resize, videoData, originalVideo, getImageWidth]);
};

export const Wrapper = ({
  aspectRatio,
  aspectMatch,
  placeholder,
  className,
  caption,
  children,
  promoState,
  video,
  imageFetchPriority,
  imageLoading,
  buildPromoImagePropsOverride
}) => {
  const {
    originalVideo,
    videoData: videoDataContext,
    videoApiDataFailed
  } = useVideoDataContext();
  const videoData = videoDataContext || video;
  const { isOpenSticky } = useStickyVideoContext();
  const { playerState } = useOrcaCommonContext();

  const { image: placeholderImage, resize, blur, color } = placeholder || {};

  const useResponsiveImage = useMemo(
    () => placeholder?.responsive === true,
    [placeholder]
  );

  const getBgImageUrl = useBackgroundImage({
    placeholderImage,
    resize,
    videoData,
    originalVideo
  });

  const bgImageUrl = useMemo(
    () => (useResponsiveImage ? null : getBgImageUrl()),
    [useResponsiveImage, getBgImageUrl]
  );

  const responsiveBgImage = useMemo(
    () =>
      useResponsiveImage
        ? getWrapperBackgroundImageProps(
            placeholder,
            videoData,
            originalVideo,
            buildPromoImagePropsOverride
          )
        : null,
    [
      useResponsiveImage,
      placeholder,
      videoData,
      originalVideo,
      buildPromoImagePropsOverride
    ]
  );

  const hasBackground = useMemo(
    () => (useResponsiveImage ? responsiveBgImage : bgImageUrl),
    [useResponsiveImage, responsiveBgImage, bgImageUrl]
  );

  const shouldShowBackgroundImage = useMemo(() => {
    return (
      hasBackground &&
      (promoState !== PromoState.HIDDEN || !playerState.isStarted)
    );
  }, [hasBackground, promoState, playerState]);

  const calcCss = useCalcCss({
    isOpenSticky,
    aspectRatio,
    aspectMatch,
    blur,
    color,
    promoState,
    playerState
  });

  return (
    <figure className={className} style={{ margin: 0 }}>
      <div
        data-testid="orca-wrapper"
        style={{
          backgroundImage:
            !useResponsiveImage && shouldShowBackgroundImage
              ? `url(${bgImageUrl})`
              : "none",
          ...calcCss()
        }}
      >
        {useResponsiveImage && shouldShowBackgroundImage && !isOpenSticky && (
          <img
            src={responsiveBgImage.src}
            srcSet={responsiveBgImage.srcSet}
            sizes={responsiveBgImage.sizes}
            alt=""
            aria-hidden
            decoding="async"
            data-testid="orca-wrapper-background-image"
            // React 18 warns on camelCase fetchPriority; HTML/SSR need lowercase.
            // eslint-disable-next-line react/no-unknown-property -- fetchpriority until React 19
            fetchpriority={imageFetchPriority}
            loading={imageLoading}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectPosition: "center",
              objectFit: aspectMatch ? "cover" : "contain",
              pointerEvents: "none"
            }}
          />
        )}
        {videoApiDataFailed && <ErrorHandler />}
        {videoData && children}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

Wrapper.propTypes = {
  /** Aspect ratio float, e.g. 0.5625 for 16:9 */
  aspectRatio: PropTypes.number,
  /** Player & stream aspect ratios match */
  aspectMatch: PropTypes.bool,
  /** Placeholder object */
  placeholder: PropTypes.object,
  /** React prop for class */
  className: PropTypes.string,
  /** Video caption */
  caption: PropTypes.node,
  children: PropTypes.node,
  video: PropTypes.object,
  imageFetchPriority: PropTypes.string,
  imageLoading: PropTypes.string,
  buildPromoImagePropsOverride: PropTypes.func,
  videoApiDataFailed: PropTypes.bool,
  videoID: PropTypes.string,
  isStickyEnabled: PropTypes.bool,
  isOpenSticky: PropTypes.bool,
  promoState: PropTypes.string,
  playerState: PropTypes.object
};
